import { Form, Button, Alert } from 'react-bootstrap';
import { connect } from 'react-redux';
import { setIsLoading } from '../general/actions';
import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from '../general/axiosInstance';
import { HOST } from '../general/utils';
import AgreementList from './agreementList';

const KNOWN_CITIES = [
    { id: '05001', name: 'Medellin' },
    { id: '11001', name: 'Bogota D.C.' },
];

// Etiquetas legibles para las feature flags conocidas (organization_features).
// Una key sin entrada aqui se muestra igual, con su nombre tecnico tal cual.
const FEATURE_FLAG_INFO = {
    budget_hard_block: {
        label: 'Bloqueo de presupuesto',
        icon: 'fa-ban',
        describe: (v) => `Bloquea la creacion o edicion de un pedido cuando el fondo con el que se paga alcanza el ${Math.round((v?.threshold ?? 0.98) * 100)}% de ejecucion presupuestal (el pedido se rechaza, no queda a medias)`,
        where: 'Se activa al crear o modificar un pedido: menu Pedidos > Crear / Modificar',
    },
    budget_semaphore: {
        label: 'Semaforo de presupuesto',
        icon: 'fa-circle',
        describe: (v) => {
            const w = Math.round((v?.warning ?? 0.60) * 100);
            const d = Math.round((v?.danger ?? 0.85) * 100);
            return `Cambia el color de las barras de consumo de fondos a verde/azul/rojo (en vez del esquema estandar verde/amarillo/rojo): verde hasta ${w}%, azul ${w + 1}-${d}%, rojo desde ${d + 1}%`;
        },
        where: 'Se ve en: Inicio (tarjeta de fondos), modal "Ver mis fondos" e Informes > Fondos',
    },
    order_role_reports: {
        label: 'Reportes visibles para rol Comprador',
        icon: 'fa-bar-chart',
        describe: () => 'Habilita que el rol Comprador (customer_order) vea los reportes de presupuesto y consumo que por defecto solo ven los roles Admin, Aprobador y Lector',
        where: 'Se ve en: menu Informes > Fondos, Consumo por Departamento, Consolidado Mes y Analisis de Productos',
    },
};

// Campos editables por flag (en porcentaje 0-100 para la UI; se convierten
// a fraccion 0-1 al guardar, que es como los espera el backend). Una flag
// sin campos (como order_role_reports) solo tiene el switch de Activo.
const FEATURE_FLAG_FIELDS = {
    budget_hard_block: [
        { key: 'threshold', label: 'Bloquear al alcanzar (%)', min: 50, max: 100, default: 98 },
    ],
    budget_semaphore: [
        { key: 'warning', label: 'Umbral azul (%)', min: 0, max: 100, default: 60 },
        { key: 'danger', label: 'Umbral rojo (%)', min: 0, max: 100, default: 85 },
    ],
    order_role_reports: [],
};

function draftFromFeature(key, feature) {
    const fields = FEATURE_FLAG_FIELDS[key] || [];
    const draft = { is_active: feature ? !!feature.is_active : true };
    fields.forEach(f => {
        const raw = feature?.value?.[f.key];
        draft[f.key] = raw !== undefined && raw !== null ? Math.round(raw * 100) : f.default;
    });
    return draft;
}

function draftToValue(key, draft) {
    const fields = FEATURE_FLAG_FIELDS[key] || [];
    const value = { enabled: draft.is_active };
    fields.forEach(f => { value[f.key] = Number(draft[f.key]) / 100; });
    return value;
}

function Organization(props) {
    const { setIsLoading } = props;

    const [messageType, setMessageType] = useState('');
    const [message, setMessage] = useState('');
    const [organization, setOrganization] = useState({ name: '', phone: '', ext_id: '', address: '', city_id: '' });

    // Feature flags — edicion inline
    const [editingFlagKey, setEditingFlagKey] = useState(null);
    const [flagDraft, setFlagDraft] = useState(null);
    const [savingFlag, setSavingFlag] = useState(false);
    const [newFlagKey, setNewFlagKey] = useState('');
    const [flagMsg, setFlagMsg] = useState({ text: '', type: '' });

    const editFeature = (feature) => {
        setEditingFlagKey(feature.key);
        setFlagDraft(draftFromFeature(feature.key, feature));
        setFlagMsg({ text: '', type: '' });
    };

    const addFeature = () => {
        if (!newFlagKey) return;
        setEditingFlagKey(newFlagKey);
        setFlagDraft(draftFromFeature(newFlagKey, null));
        setFlagMsg({ text: '', type: '' });
    };

    const cancelEditFeature = () => {
        setEditingFlagKey(null);
        setFlagDraft(null);
    };

    const saveFeature = async () => {
        const fields = FEATURE_FLAG_FIELDS[editingFlagKey] || [];

        if (flagDraft.is_active && editingFlagKey === 'budget_semaphore' &&
            Number(flagDraft.danger) <= Number(flagDraft.warning)) {
            setFlagMsg({ text: 'El umbral rojo debe ser mayor al umbral azul', type: 'danger' });
            return;
        }

        // El resto del sistema (colors.js, navigation.js, OrderController) lee
        // el "enabled" de adentro del JSON `value`, no la columna is_active de
        // la fila — se mantienen sincronizados para que un solo switch alcance.
        const value = draftToValue(editingFlagKey, flagDraft);

        setSavingFlag(true);
        setFlagMsg({ text: '', type: '' });
        try {
            const response = await axiosInstance.put(`/organization-features/${editingFlagKey}`, {
                value,
                is_active: flagDraft.is_active,
            });
            setOrganization(prev => {
                const others = (prev.features || []).filter(f => f.key !== editingFlagKey);
                return { ...prev, features: [...others, response.data] };
            });
            setFlagMsg({ text: 'Configuracion guardada correctamente', type: 'success' });
            setEditingFlagKey(null);
            setFlagDraft(null);
            setNewFlagKey('');
        } catch (err) {
            const apiMsg = err?.response?.data?.errors
                ? Object.values(err.response.data.errors).flat().join(' ')
                : err?.response?.data?.message;
            setFlagMsg({ text: apiMsg || 'Error al guardar la configuracion', type: 'danger' });
        } finally {
            setSavingFlag(false);
        }
    };

    // Logo
    const fileInputRef = useRef(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [logoMsg, setLogoMsg] = useState({ text: '', type: '' });
    const [logoTimestamp, setLogoTimestamp] = useState(Date.now());

    const orgLogoUrl = organization.id
        ? `${HOST}storage/images/logos/organization-${organization.id}.jpg?t=${logoTimestamp}`
        : null;

    const getOrganization = () => {
        if (organization.name) return;
        setIsLoading(true);
        axiosInstance.get('/organizations/current')
            .then(response => { setOrganization(response.data); })
            .catch(error => { setMessageType('danger'); setMessage(error.response.data.message); })
            .finally(() => setIsLoading(false));
    };

    const onFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
        setLogoMsg({ text: '', type: '' });
    };

    const uploadLogo = async () => {
        if (!logoFile) return;
        setUploadingLogo(true);
        setLogoMsg({ text: '', type: '' });
        try {
            const formData = new FormData();
            formData.append('image', logoFile);
            await axiosInstance.post(`/organizations/${organization.id}/logo`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setLogoMsg({ text: 'Logo actualizado correctamente', type: 'success' });
            setLogoFile(null);
            setLogoPreview(null);
            setLogoTimestamp(Date.now());
        } catch (err) {
            setLogoMsg({ text: err?.response?.data?.message || 'Error al subir el logo', type: 'danger' });
        } finally {
            setUploadingLogo(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const cancelLogoPreview = () => {
        setLogoFile(null);
        setLogoPreview(null);
        setLogoMsg({ text: '', type: '' });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const update = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await axiosInstance.put('/organizations/' + organization.id, organization);
            setMessageType('success');
            setMessage('Informacion actualizada correctamente');
        } catch (error) {
            setMessageType('danger');
            setMessage(error.response.data.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setMessage('');
        if (!organization.name) getOrganization();
    }, []);

    const field = (label, key, type = 'text') => (
        <div style={{ flex: 1, minWidth: 180 }}>
            <Form.Label className="report-field-label">{label}</Form.Label>
            <Form.Control
                type={type}
                value={organization[key] || ''}
                required
                onChange={(e) => setOrganization({ ...organization, [key]: e.target.value })}
            />
        </div>
    );

    const existingFlagKeys = (organization.features || []).map(f => f.key);
    const availableFlagKeys = Object.keys(FEATURE_FLAG_INFO).filter(k => !existingFlagKeys.includes(k));

    const renderFeatureRow = (flagKey, feature) => {
        const info = FEATURE_FLAG_INFO[flagKey];
        const isEditing = editingFlagKey === flagKey;

        if (isEditing) {
            const fields = FEATURE_FLAG_FIELDS[flagKey] || [];
            return (
                <div key={flagKey} style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid var(--abi-primary)', background: '#eff6ff' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <i className={`fa ${info?.icon || 'fa-toggle-on'}`} style={{ color: 'var(--abi-primary)', fontSize: 16, width: 18, textAlign: 'center', marginTop: 2 }} />
                        <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{info?.label || flagKey}</div>
                            {info?.describe && (
                                <div style={{ fontSize: 12, color: 'var(--abi-muted)', marginTop: 2 }}>{info.describe(draftToValue(flagKey, flagDraft))}</div>
                            )}
                            {info?.where && (
                                <div style={{ fontSize: 11, color: 'var(--abi-primary)', marginTop: 3 }}>
                                    <i className="fa fa-map-marker" /> {info.where}
                                </div>
                            )}
                        </div>
                        <Form.Check
                            type="switch"
                            id={`flag-active-${flagKey}`}
                            label={flagDraft.is_active ? 'Activo' : 'Inactivo'}
                            checked={flagDraft.is_active}
                            onChange={(e) => setFlagDraft({ ...flagDraft, is_active: e.target.checked })}
                        />
                    </div>
                    {fields.length > 0 && (
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, marginLeft: 28 }}>
                            {fields.map(f => (
                                <div key={f.key} style={{ width: 170 }}>
                                    <Form.Label className="report-field-label" style={{ fontSize: 11 }}>{f.label}</Form.Label>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        min={f.min}
                                        max={f.max}
                                        value={flagDraft[f.key]}
                                        disabled={!flagDraft.is_active}
                                        onChange={(e) => setFlagDraft({ ...flagDraft, [f.key]: e.target.value })}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, marginLeft: 28 }}>
                        <Button size="sm" variant="primary" onClick={saveFeature} disabled={savingFlag}>
                            {savingFlag
                                ? <span><i className="fa fa-spinner fa-spin" /> Guardando...</span>
                                : <span><i className="fa fa-check" /> Guardar</span>}
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={cancelEditFeature} disabled={savingFlag}>
                            Cancelar
                        </Button>
                    </div>
                </div>
            );
        }

        return (
            <div
                key={feature.id || flagKey}
                style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 10,
                    border: '1px solid var(--abi-border)',
                    background: feature.is_active ? '#f8fafc' : '#fafafa',
                }}
            >
                <i className={`fa ${info?.icon || 'fa-toggle-on'}`} style={{ color: feature.is_active ? 'var(--abi-primary)' : '#9ca3af', fontSize: 16, width: 18, textAlign: 'center' }} />
                <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{info?.label || flagKey}</div>
                    {info?.describe && (
                        <div style={{ fontSize: 12, color: 'var(--abi-muted)', marginTop: 2 }}>
                            {info.describe(feature.value)}
                        </div>
                    )}
                    {info?.where && (
                        <div style={{ fontSize: 11, color: 'var(--abi-primary)', marginTop: 3 }}>
                            <i className="fa fa-map-marker" /> {info.where}
                        </div>
                    )}
                </div>
                <span className="report-status-badge" style={feature.is_active
                    ? { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' }
                    : { background: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }
                }>
                    {feature.is_active ? 'Activo' : 'Inactivo'}
                </span>
                <Button size="sm" variant="outline-primary" onClick={() => editFeature(feature)}>
                    <i className="fa fa-pencil-square-o" />
                </Button>
            </div>
        );
    };

    return (
        <>
            {/* Header */}
            <div className="order-page-header">
                <div>
                    <div className="order-page-title">
                        <i className="fa fa-building" style={{ color: 'var(--abi-primary)', fontSize: 18 }} />
                        {organization.name || 'Entidad'}
                        {organization.id && (
                            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--abi-muted)' }}>
                                #{organization.id} &middot; {organization.ext_id}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <Form onSubmit={update}>
                {/* Informacion general */}
                <div className="order-section">
                    <div className="order-section-header">
                        <i className="fa fa-id-card-o" /> Informacion general
                    </div>
                    <div className="order-section-body">
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                            {field('NIT / Ext ID', 'ext_id')}
                            {field('Nombre de la entidad', 'name')}
                        </div>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
                            {field('Telefono', 'phone')}
                            {field('Direccion', 'address')}
                        </div>
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4 }}>
                            <div style={{ flex: 1, minWidth: 180 }}>
                                <Form.Label className="report-field-label">Ciudad</Form.Label>
                                <Form.Select
                                    value={organization.city_id || ''}
                                    onChange={(e) => setOrganization({ ...organization, city_id: e.target.value || null })}
                                >
                                    <option value="">Sin ciudad</option>
                                    {KNOWN_CITIES.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </Form.Select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Logo */}
                {organization.id && (
                    <div className="order-section" style={{ marginTop: 16 }}>
                        <div className="order-section-header">
                            <i className="fa fa-image" /> Logo de la organizacion
                        </div>
                        <div className="order-section-body">
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>

                                {/* Imagen actual o preview */}
                                <div style={{ flexShrink: 0 }}>
                                    <div style={{
                                        width: 140, height: 140, borderRadius: 12,
                                        border: '1px solid var(--abi-border)',
                                        background: '#f8fafc',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        overflow: 'hidden',
                                    }}>
                                        <img
                                            key={logoPreview || logoTimestamp}
                                            src={logoPreview || orgLogoUrl}
                                            alt="Logo"
                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                        <div style={{
                                            display: 'none', flexDirection: 'column',
                                            alignItems: 'center', color: 'var(--abi-muted)', fontSize: 12,
                                        }}>
                                            <i className="fa fa-image" style={{ fontSize: 32, marginBottom: 6, opacity: 0.3 }} />
                                            Sin logo
                                        </div>
                                    </div>
                                    {logoPreview && (
                                        <div style={{ fontSize: 11, color: 'var(--abi-muted)', textAlign: 'center', marginTop: 4 }}>
                                            Vista previa
                                        </div>
                                    )}
                                </div>

                                {/* Controles de subida */}
                                <div style={{ flex: 1, minWidth: 200 }}>
                                    <p style={{ fontSize: 13, color: 'var(--abi-muted)', marginBottom: 12 }}>
                                        Formatos admitidos: JPG, PNG, WEBP. Maximo 4 MB.
                                        La imagen se redimensionara automaticamente a 720 px de ancho.
                                    </p>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        style={{ display: 'none' }}
                                        onChange={onFileChange}
                                    />
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        <Button
                                            size="sm"
                                            variant="outline-primary"
                                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                            disabled={uploadingLogo}
                                        >
                                            <i className="fa fa-folder-open-o" /> Seleccionar imagen
                                        </Button>
                                        {logoFile && (
                                            <>
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    onClick={uploadLogo}
                                                    disabled={uploadingLogo}
                                                >
                                                    {uploadingLogo
                                                        ? <span><i className="fa fa-spinner fa-spin" /> Subiendo...</span>
                                                        : <span><i className="fa fa-upload" /> Subir logo</span>
                                                    }
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    onClick={cancelLogoPreview}
                                                    disabled={uploadingLogo}
                                                >
                                                    <i className="fa fa-times" /> Cancelar
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                    {logoFile && (
                                        <div style={{ fontSize: 12, color: 'var(--abi-muted)', marginTop: 8 }}>
                                            <i className="fa fa-file-image-o" /> {logoFile.name}
                                        </div>
                                    )}
                                    {logoMsg.text && (
                                        <Alert variant={logoMsg.type} className="py-2 mt-3" style={{ fontSize: 13 }}>
                                            {logoMsg.text}
                                        </Alert>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {message && (
                    <Alert variant={messageType || 'warning'} className="py-2" style={{ fontSize: 13 }}>
                        {message}
                    </Alert>
                )}

                <div className="order-form-footer">
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="primary" type="submit" size="sm">
                            <i className="fa fa-save" /> Guardar cambios
                        </Button>
                    </div>
                </div>
            </Form>

            {/* Feature flags */}
            {organization.id && (
                <div className="order-section" style={{ marginTop: 16 }}>
                    <div className="order-section-header">
                        <i className="fa fa-flag-o" /> Configuraciones especiales (feature flags)
                    </div>
                    <div className="order-section-body">
                        {flagMsg.text && (
                            <Alert variant={flagMsg.type || 'warning'} className="py-2" style={{ fontSize: 13 }}>
                                {flagMsg.text}
                            </Alert>
                        )}

                        {(!organization.features || organization.features.length === 0) && !editingFlagKey && (
                            <p style={{ fontSize: 13, color: 'var(--abi-muted)', margin: '0 0 10px' }}>
                                Esta entidad no tiene configuraciones especiales activas. Usa el comportamiento estandar de la plataforma.
                            </p>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {(organization.features || []).map((feature) => renderFeatureRow(feature.key, feature))}
                            {editingFlagKey && !existingFlagKeys.includes(editingFlagKey) && renderFeatureRow(editingFlagKey, null)}
                        </div>

                        {availableFlagKeys.length > 0 && !editingFlagKey && (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                                <Form.Select
                                    size="sm"
                                    style={{ maxWidth: 300 }}
                                    value={newFlagKey}
                                    onChange={(e) => setNewFlagKey(e.target.value)}
                                >
                                    <option value="">Agregar configuracion...</option>
                                    {availableFlagKeys.map(k => (
                                        <option key={k} value={k}>{FEATURE_FLAG_INFO[k].label}</option>
                                    ))}
                                </Form.Select>
                                <Button size="sm" variant="outline-primary" disabled={!newFlagKey} onClick={addFeature}>
                                    <i className="fa fa-plus" /> Agregar
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Contratos */}
            <div className="order-section" style={{ marginTop: 20, overflowX: 'auto' }}>
                <AgreementList />
            </div>
        </>
    );
}

function mapStateToProps(state) { return { isLoading: state.isLoading }; }
const mapDispatchToProps = { setIsLoading };
export default connect(mapStateToProps, mapDispatchToProps)(Organization);
